import React from 'react';

import { getJSON, postJSON } from '../../api/client.js';
import {
  CrudPage, UniActionButton, UniPageShell, UniSection, UniStatusPill, UniTable, useUniList,
} from '../waba/WabaShared.jsx';
import { SB_API, THEME, HOME, EasyField, ExistingPicker, TechDetails, WizardShell, useWizard } from './SignalBridgeShared.jsx';

const P = { theme: THEME, api: SB_API, home: HOME };

const DOT = { HEALTHY: '#16a34a', WARNING: '#d97706', CRITICAL: '#dc2626', UNKNOWN: '#94a3b8' };

export function SignalBridgePixelGroups() {
  const dests = useUniList(`${SB_API}/destinations/`);
  const opts = (dests.rows || []).filter((d) => d.destination_type === 'META_CAPI').map((d) => ({ value: d.id, label: d.name }));
  return (
    <>
      <p className="uni-num--muted" style={{ margin: '0 24px 8px' }}>
        Meta destination ids for member_ids JSON: {opts.length ? opts.map((o) => `${o.label} = ${o.value}`).join(' · ') : 'create a Meta destination first'}
      </p>
    <CrudPage
      {...P} helpPage="pixel_groups" icon="▦" title="Pixel groups"
      endpoint={`${SB_API}/pixel-groups/`}
      createLabel="+ Group"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'member_ids', label: 'Meta destinations (JSON list of ids)', type: 'json', hint: 'Adding a pixel here includes it in every route that targets this group.' },
        { key: 'is_active', label: 'Active', type: 'check' },
      ]}
      columns={[
        { key: 'name', label: 'Name', render: (r) => <strong>{r.name}</strong> },
        { key: 'member_names', label: 'Members', render: (r) => (r.member_names || []).join(', ') },
        { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
      ]}
    />
    </>
  );
}

export function SignalBridgeGoogleWizard({ embedded, onFinished, forceRef }) {
  const { state, busy, data, setField, act } = useWizard('google-ads', { onFinished, forceRef });
  const step = state?.current_step || 1;
  const mappings = data.mappings || (state?.default_events || ['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout', 'Purchase']).map((et) => ({
    event_type: et, is_enabled: et === 'Purchase', conversion_label: '', conversion_action_resource_name: '', send_value: true,
  }));
  const setMap = (eventType, patch) => {
    const next = mappings.map((m) => (m.event_type === eventType ? { ...m, ...patch } : m));
    setField('mappings', next);
  };
  const editing = state?.mode === 'edit' || !!data.destination_id;
  const actWithMaps = (action, extra = {}) => act(action, { mappings, ...extra });
  return (
    <WizardShell
      embedded={embedded}
      title={editing ? 'Edit Google Ads' : 'Connect Google Ads'}
      subtitle="Conversion ID and labels from Google Ads. No code on your store — we send conversions from the server."
      helpPage="google_wizard"
      state={state}
      busy={busy}
      act={actWithMaps}
    >
      <ExistingPicker
        items={state?.existing?.google_ads}
        value={data.destination_id}
        onSelect={(id) => act('load_existing', { destination_id: id, mappings })}
        onCreateNew={() => act('start_new')}
        label="Previously saved Google Ads destinations"
      />
      {step === 1 && (
        <>
          <EasyField label="Name" hint="e.g. Google Ads — main account">
            <input value={data.name || ''} onChange={(e) => setField('name', e.target.value)} />
          </EasyField>
          <EasyField label="Conversion ID" hint="Looks like AW-XXXXXXXXXX. From Google Ads → Goals → Conversions → Tag setup.">
            <input value={data.conversion_id || ''} onChange={(e) => setField('conversion_id', e.target.value)} placeholder="AW-XXXXXXXXXX" />
          </EasyField>
        </>
      )}
      {step === 2 && (
        <>
          <p className="uni-num--muted">{state.help?.labels}</p>
          <UniTable
            rows={mappings} rowKey="event_type" empty="No mappings"
            columns={[
              { key: 'event_type', label: 'Event' },
              { key: 'is_enabled', label: 'On', render: (r) => <input type="checkbox" checked={!!r.is_enabled} onChange={(e) => setMap(r.event_type, { is_enabled: e.target.checked })} /> },
              { key: 'conversion_label', label: 'Label', render: (r) => <input value={r.conversion_label || ''} onChange={(e) => setMap(r.event_type, { conversion_label: e.target.value })} /> },
              { key: 'conversion_action_resource_name', label: 'API resource', render: (r) => <input value={r.conversion_action_resource_name || ''} onChange={(e) => setMap(r.event_type, { conversion_action_resource_name: e.target.value })} placeholder="customers/…/conversionActions/…" /> },
              { key: 'send_value', label: 'Value', render: (r) => <input type="checkbox" checked={!!r.send_value} onChange={(e) => setMap(r.event_type, { send_value: e.target.checked })} /> },
            ]}
          />
        </>
      )}
      {step === 3 && (
        <>
          <label>Channel
            <select value={data.channel_mode || 'BROWSER_ONLY'} onChange={(e) => setField('channel_mode', e.target.value)}>
              <option value="BROWSER_ONLY">Browser snippet only</option>
              <option value="SERVER_ONLY">Server API only</option>
              <option value="BOTH">Browser + server</option>
            </select>
          </label>
          {(data.channel_mode || 'BROWSER_ONLY') !== 'BROWSER_ONLY' && (
            <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
              {data.destination_id && (
                <EasyField label="Keep credentials already saved">
                  <input type="checkbox" checked={data.keep_existing_credentials !== false} onChange={(e) => setField('keep_existing_credentials', e.target.checked)} />
                </EasyField>
              )}
              <input placeholder={data.credentials_set ? 'Developer token (leave blank to keep)' : 'Developer token'} value={data.developer_token || ''} onChange={(e) => setField('developer_token', e.target.value)} />
              <input placeholder="OAuth client id" value={data.oauth_client_id || ''} onChange={(e) => setField('oauth_client_id', e.target.value)} />
              <input placeholder="OAuth client secret" type="password" value={data.oauth_client_secret || ''} onChange={(e) => setField('oauth_client_secret', e.target.value)} />
              <input placeholder="OAuth refresh token" type="password" value={data.oauth_refresh_token || ''} onChange={(e) => setField('oauth_refresh_token', e.target.value)} />
              <input placeholder="login_customer_id (MCC, optional)" value={data.login_customer_id || ''} onChange={(e) => setField('login_customer_id', e.target.value)} />
              <input placeholder="customer_id" value={data.customer_id || ''} onChange={(e) => setField('customer_id', e.target.value)} />
              {state.validation && <TechDetails title="OAuth check" data={state.validation} />}
            </div>
          )}
        </>
      )}
      {step === 4 && (
        <>
          <UniActionButton hero onClick={() => actWithMaps('preview')}>Refresh preview</UniActionButton>
          {' '}
          <UniActionButton hero onClick={() => actWithMaps('validate')}>Validate-only test upload</UniActionButton>
          <p className="uni-num--muted" style={{ marginTop: 12 }}>If you use the browser collector, paste this short script as a Shopify custom pixel. The collector file is shared. Browser collect has the theme.liquid script for each store.</p>
          <TechDetails title="Snippet" data={state.preview?.snippet || ''} />
          <TechDetails title="Test upload" data={state.test_upload} />
        </>
      )}
      {step === 5 && (
        <p>{state.created ? `Saved ${state.created.name}` : 'Finish writes the destination, mappings, and a default event route.'}</p>
      )}
    </WizardShell>
  );
}

export function SignalBridgeHealthEngine() {
  const { raw, loading, error, reload } = useUniList(`${SB_API}/health-engine/`);
  const [running, setRunning] = React.useState(false);
  const [live, setLive] = React.useState(null);
  const run = async () => {
    setRunning(true);
    try {
      const data = await postJSON(`${SB_API}/health-engine/`, {});
      setLive(data);
      reload && reload();
    } finally {
      setRunning(false);
    }
  };
  return (
    <UniPageShell
      {...P} icon="💚" title="Connection health" helpPage="health" helpApi={SB_API}
      actions={(
        <>
          <UniActionButton hero primary disabled={running} onClick={run}>{running ? 'Running…' : 'Run checks now'}</UniActionButton>
          <UniActionButton hero to={HOME}>← Home</UniActionButton>
        </>
      )}
    >
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      <p className="uni-num--muted">Snippet version {raw?.current_snippet_version}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
        {(raw?.destinations || []).map((d) => (
          <div key={d.id} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 99, background: DOT[d.status] || DOT.UNKNOWN }} />
              <strong>{d.name}</strong>
            </div>
            <div className="uni-num--muted">{d.destination_type}</div>
            {(d.checks || []).map((c) => (
              <div key={c.id} style={{ marginTop: 8, fontSize: 13 }}>
                <UniStatusPill value={c.status} /> {c.check_type}
                <div className="uni-num--muted">{c.how_to_fix}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <UniSection title="Stores" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginTop: 12 }}>
        {(raw?.stores || []).map((d) => (
          <div key={d.id} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 99, background: DOT[d.status] || DOT.UNKNOWN }} />
              <strong>{d.name}</strong>
            </div>
            {(d.checks || []).map((c) => (
              <div key={c.id || c.check_type} style={{ marginTop: 8, fontSize: 13 }}>
                <UniStatusPill value={c.status} /> {c.check_type}
                <div className="uni-num--muted">{c.how_to_fix}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
      {(raw?.merge || []).map((c) => (
        <div key={c.id || c.check_type} style={{ marginTop: 12, fontSize: 13 }}>
          <UniStatusPill value={c.status} /> {c.check_type}
          <div className="uni-num--muted">{c.how_to_fix}</div>
        </div>
      ))}
      {live && <pre className="uni-note" style={{ marginTop: 16 }}>{JSON.stringify(live, null, 2)}</pre>}
    </UniPageShell>
  );
}

export function SignalBridgeJourney() {
  const [q, setQ] = React.useState('');
  const [raw, setRaw] = React.useState(null);
  const search = async () => {
    const data = await getJSON(`${SB_API}/journey/?q=${encodeURIComponent(q)}`);
    setRaw(data);
  };
  return (
    <UniPageShell {...P} icon="↕" title="Order journey" helpPage="journey" helpApi={SB_API} actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Order id, event id, email, or phone" style={{ flex: 1 }} />
        <UniActionButton hero primary onClick={search}>Search</UniActionButton>
      </div>
      <UniTable
        rows={raw?.timeline || []} empty="Search to load a timeline" rowKey={(r, i) => `${r.kind}-${r.id}-${i}`}
        columns={[
          { key: 'at', label: 'When' },
          { key: 'kind', label: 'Kind', render: (r) => <UniStatusPill value={r.kind} /> },
          { key: 'source', label: 'Source', render: (r) => r.source || r.destination || r.event_type || '' },
          { key: 'status', label: 'Status', render: (r) => <UniStatusPill value={r.status} /> },
          { key: 'error', label: 'Detail' },
        ]}
      />
    </UniPageShell>
  );
}

export function SignalBridgeDlq() {
  const list = useUniList(`${SB_API}/incoming/?processing_status=ERROR`);
  const [msg, setMsg] = React.useState(null);
  const replayOne = async (row) => {
    const data = await postJSON(`${SB_API}/incoming/${row.id}/replay/`, {});
    setMsg(data);
    list.reload && list.reload();
  };
  const bulk = async (fp) => {
    const preview = await postJSON(`${SB_API}/incoming/bulk-replay/`, { error_fingerprint: fp, confirm: false });
    if (!window.confirm(`Re-run ${preview.count} events with fingerprint “${fp}”?`)) return;
    const data = await postJSON(`${SB_API}/incoming/bulk-replay/`, { error_fingerprint: fp, confirm: true });
    setMsg(data);
    list.reload && list.reload();
  };
  return (
    <UniPageShell {...P} icon="📭" title="Dead letter queue" helpPage="dlq" helpApi={SB_API} actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}>
      {msg && <pre className="uni-note">{JSON.stringify(msg, null, 2)}</pre>}
      <UniTable
        rows={list.rows} loading={list.loading} empty="No failed incoming events" rowKey="id"
        columns={[
          { key: 'received_at', label: 'When' },
          { key: 'source', label: 'Source' },
          { key: 'error_fingerprint', label: 'Fingerprint' },
          { key: 'error_detail', label: 'Error' },
          { key: 'act', label: '', render: (r) => (
            <>
              <button type="button" className="ty-btn ty-btn--secondary" onClick={() => replayOne(r)}>Re-run</button>
              {' '}
              {r.error_fingerprint && <button type="button" className="ty-btn ty-btn--secondary" onClick={() => bulk(r.error_fingerprint)}>Bulk this error</button>}
            </>
          ) },
        ]}
      />
    </UniPageShell>
  );
}

export function SignalBridgeCompare() {
  const { raw, loading, error } = useUniList(`${SB_API}/reports/?type=PIXEL_COMPARE`);
  const retry = async () => {
    const ids = (raw?.inconsistent_orders || []).map((r) => r.event_id);
    if (!ids.length) return;
    const preview = await postJSON(`${SB_API}/retry-inconsistent/`, { event_ids: ids, confirm: false });
    if (!window.confirm(`Retry ${preview.failed_attempts} failed attempts across ${preview.events} events?`)) return;
    await postJSON(`${SB_API}/retry-inconsistent/`, { event_ids: ids, confirm: true });
  };
  return (
    <UniPageShell
      {...P} icon="▤" title="Pixel comparison" helpPage="pixel_compare" helpApi={SB_API}
      actions={(
        <>
          <UniActionButton hero onClick={retry}>Retry inconsistent</UniActionButton>
          <UniActionButton hero to={HOME}>← Home</UniActionButton>
        </>
      )}
    >
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      <UniTable
        rows={raw?.destinations || []} empty="No destinations" rowKey="id"
        columns={[
          { key: 'name', label: 'Destination' },
          { key: 'destination_type', label: 'Type' },
          { key: 'events_sent', label: 'Sent', align: 'right' },
          { key: 'success_rate_pct', label: 'Success %', align: 'right' },
          { key: 'rejected', label: 'Rejected', align: 'right' },
          { key: 'avg_emq_fields', label: 'Avg EMQ fields', align: 'right' },
        ]}
      />
      <UniSection title={`Inconsistent orders (${raw?.inconsistent_count || 0})`} />
      <UniTable
        rows={raw?.inconsistent_orders || []} empty="All Meta pixels agree" rowKey="event_id"
        columns={[
          { key: 'event_id', label: 'Event id' },
          { key: 'statuses', label: 'Per dest', render: (r) => JSON.stringify(r.statuses) },
        ]}
      />
    </UniPageShell>
  );
}
